import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { can } from '../lib/permissions';
import { QASkeleton } from '../components/QASkeleton';
import { QAEmpty } from '../components/QAEmpty';
import { formatDateAU } from '../../../shared/format';
import { mergeSettings, type PracticeSettings } from '../../../shared/practiceSettings';
import { ComplianceRulesPanel } from '../components/ComplianceRulesPanel';
import { SEGMENTS } from '../../../shared/types';
import type { TodayItem, TodaySection, TodayView } from '../../../shared/today';

/**
 * Today: calls due, fee consents, overdue reviews, advice to write, the week's
 * calls and birthdays, from the same book the landing page shows. Calls can be
 * logged from here. Admins set call cadence, consent timing and the morning
 * email in the settings panel.
 */
export function Today() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['today'], queryFn: () => api<{ data: TodayView }>('/api/today') });
  const [showSettings, setShowSettings] = useState(false);
  // Page-level notice: logging a call moves the client out of "Calls due", which
  // unmounts its row, so the confirmation can't live on the row.
  const [notice, setNotice] = useState<string | null>(null);
  const canEdit = can(user?.role, 'client.edit');
  const isAdmin = can(user?.role, 'users.manage');

  if (q.isLoading) return <QASkeleton variant="dashboard" />;
  if (q.isError || !q.data) return <div className="qa-badge qa-badge--out">Unable to load Today</div>;
  const view = q.data.data;
  const d = view.date;

  return (
    <div>
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4 qa-rise qa-stagger-1">
        <div>
          <p className="qa-eyebrow mb-2">Today · {formatDateAU(d)}</p>
          <h1 className="qa-h1">What needs <span className="qa-italic-accent">doing.</span></h1>
          <p className="qa-deck mt-2 text-ink-soft">
            {view.total === 0 ? 'Nothing is due today.' : view.sections.filter((s) => s.items.length).map((s) => `${s.title}: ${s.items.length}`).join(' · ')}
          </p>
        </div>
        {isAdmin && (
          <button type="button" className="btn-ghost text-xs self-start md:self-end" aria-expanded={showSettings} onClick={() => setShowSettings((s) => !s)}>
            {showSettings ? 'Hide settings' : 'Practice settings'}
          </button>
        )}
      </div>

      {showSettings && isAdmin && <SettingsPanel />}
      {showSettings && isAdmin && <ComplianceRulesLoader today={d} />}

      <div role="status" aria-live="polite" className={notice ? 'qa-attention-banner mb-4 text-sm' : 'sr-only'}>{notice}</div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 qa-rise qa-stagger-2">
        {view.sections.map((s) => (
          <Section key={s.id} section={s} canLog={canEdit && (s.id === 'calls' || s.id === 'callsWeek')}
            onLogged={(msg) => { setNotice(msg); void qc.invalidateQueries({ queryKey: ['today'] }); void qc.invalidateQueries({ queryKey: ['dashboard'] }); }} />
        ))}
      </div>
    </div>
  );
}

function Section({ section, canLog, onLogged }: { section: TodaySection; canLog: boolean; onLogged: (message: string) => void }) {
  return (
    <section className="qa-card p-5" aria-labelledby={`today-${section.id}`} data-testid={`today-${section.id}`}>
      <div className="flex items-baseline justify-between mb-3">
        <h2 id={`today-${section.id}`} className="qa-eyebrow">{section.title}</h2>
        <span className="qa-mono text-xs text-ink-mute">{section.items.length}</span>
      </div>
      {section.items.length === 0 ? (
        <QAEmpty inline title="Nothing here today." />
      ) : (
        <ul className="divide-y divide-hairline-paper">
          {section.items.slice(0, 50).map((i, n) => <Row key={`${section.id}-${i.clientId}-${n}`} item={i} canLog={canLog} onLogged={onLogged} />)}
          {section.items.length > 50 && <li className="py-2 text-xs text-ink-mute">and {section.items.length - 50} more on the book</li>}
        </ul>
      )}
    </section>
  );
}

function Row({ item, canLog, onLogged }: { item: TodayItem; canLog: boolean; onLogged: (message: string) => void }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const overdue = /overdue|Lapsed/.test(item.detail);

  async function log() {
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ data: { callDue: string | null } }>(`/api/clients/${item.clientId}/log-call`, {
        method: 'POST', body: JSON.stringify({ type: 'call', content: note.trim() }),
      });
      setOpen(false);
      setNote('');
      onLogged(r.data.callDue ? `Logged the call with ${item.name}. Next call ${formatDateAU(r.data.callDue)}.` : `Logged the call with ${item.name}.`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not log the call');
    } finally { setBusy(false); }
  }

  return (
    <li className="py-2">
      <div className="flex items-center gap-3">
        <Link to={`/clients/${item.clientId}`} className="text-sm font-medium text-ink hover:text-rust flex-1 min-w-0 truncate">{item.name}</Link>
        {item.segment && <span className="qa-mono text-[10px] text-ink-mute uppercase">{item.segment}</span>}
        <span className={`text-xs ${overdue ? 'text-rust font-medium' : 'text-ink-soft'}`}>{item.detail}</span>
        <Link to={`/clients/${item.clientId}?tab=brief`} className="qa-book-pop__btn" aria-label={`Pre-meeting brief for ${item.name}`}>Brief</Link>
        {canLog && (
          <button type="button" className="qa-book-pop__btn" aria-expanded={open} aria-label={`Log a call with ${item.name}`} onClick={() => setOpen((o) => !o)}>
            Log call
          </button>
        )}
      </div>
      {open && (
        <div className="mt-2 flex gap-2 items-start">
          <textarea className="qa-input flex-1 text-sm" rows={2} aria-label={`Call note for ${item.name}`} placeholder="What was discussed?"
            value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
          <button type="button" className="btn-primary text-xs" disabled={busy || !note.trim()} onClick={log}>Save</button>
        </div>
      )}
      {msg && <p role="alert" className="text-xs text-rust mt-1">{msg}</p>}
    </li>
  );
}

function ComplianceRulesLoader({ today }: { today: string }) {
  const q = useQuery({ queryKey: ['settings'], queryFn: () => api<{ data: PracticeSettings }>('/api/settings') });
  if (q.isLoading) return <QASkeleton variant="cards" />;
  return <ComplianceRulesPanel settings={mergeSettings(q.data?.data)} today={today} canEdit />;
}

function SettingsPanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['settings'], queryFn: () => api<{ data: PracticeSettings }>('/api/settings') });
  const [draft, setDraft] = useState<PracticeSettings | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [recipientsText, setRecipientsText] = useState<string | null>(null);
  const s = draft ?? mergeSettings(q.data?.data);
  const set = (fn: (p: PracticeSettings) => PracticeSettings) => setDraft(fn(s));

  async function save() {
    setStatus(null);
    try {
      await api('/api/settings', { method: 'PUT', body: JSON.stringify({ ...s, compliance: undefined }) });
      await Promise.all([qc.invalidateQueries({ queryKey: ['settings'] }), qc.invalidateQueries({ queryKey: ['today'] }), qc.invalidateQueries({ queryKey: ['dashboard'] })]);
      setDraft(null);
      setRecipientsText(null);
      setStatus('Saved.');
    } catch (e) { setStatus(e instanceof Error ? e.message : 'Save failed'); }
  }
  async function testSend() {
    setStatus(null);
    try {
      const r = await api<{ data: { recipients: string[] } }>('/api/today/digest-test', { method: 'POST' });
      setStatus(`Test email sent to ${r.data.recipients.join(', ')}.`);
    } catch (e) { setStatus(e instanceof Error ? e.message : 'Send failed'); }
  }

  if (q.isLoading) return null;
  return (
    <div className="qa-card p-5 mb-6" data-testid="practice-settings">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <fieldset>
          <legend className="qa-eyebrow mb-2">Call cadence (months)</legend>
          {SEGMENTS.map((seg) => (
            <label key={seg} className="flex items-center justify-between gap-3 text-sm py-1">
              <span>{seg}</span>
              <input
                type="number" min={1} max={60} className="qa-input w-20 text-right qa-mono" aria-label={`${seg} call cadence in months`}
                placeholder="none"
                value={s.callCadenceMonths[seg] ?? ''}
                onChange={(e) => set((p) => ({ ...p, callCadenceMonths: { ...p.callCadenceMonths, [seg]: e.target.value === '' ? null : Math.max(1, Math.min(60, Number(e.target.value))) } }))}
              />
            </label>
          ))}
          <p className="text-[11px] text-ink-mute mt-1">Blank = no scheduled calls for that tier.</p>
        </fieldset>
        <fieldset>
          <legend className="qa-eyebrow mb-2">Fee consent</legend>
          <label className="flex items-center justify-between gap-3 text-sm py-1">
            <span>Renewal window after due date (days)</span>
            <input type="number" min={0} max={365} className="qa-input w-20 text-right qa-mono" aria-label="Renewal window in days"
              value={s.feeConsent.renewalWindowDays}
              onChange={(e) => set((p) => ({ ...p, feeConsent: { ...p.feeConsent, renewalWindowDays: Math.max(0, Math.min(365, Number(e.target.value) || 0)) } }))} />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm py-1">
            <span>Warn before due date (days)</span>
            <input type="number" min={0} max={180} className="qa-input w-20 text-right qa-mono" aria-label="Warning days"
              value={s.feeConsent.warnDays}
              onChange={(e) => set((p) => ({ ...p, feeConsent: { ...p.feeConsent, warnDays: Math.max(0, Math.min(180, Number(e.target.value) || 0)) } }))} />
          </label>
          <p className="text-[11px] text-ink-mute mt-1">Due date = earliest OFA due date, else last consent + 12 months. Confirm the window with compliance.</p>
        </fieldset>
        <fieldset>
          <legend className="qa-eyebrow mb-2">Entity reviews (months)</legend>
          {([
            ['strategyReviewMonths', 'SMSF investment strategy', 60],
            ['auditOverdueMonths', 'SMSF audit overdue after', 36],
            ['deedReviewMonths', 'SMSF and trust deed', 120],
          ] as const).map(([k, label, max]) => (
            <label key={k} className="flex items-center justify-between gap-3 text-sm py-1">
              <span>{label}</span>
              <input type="number" min={1} max={max} className="qa-input w-20 text-right qa-mono" aria-label={`${label} in months`}
                value={s.entities[k]}
                onChange={(e) => set((p) => ({ ...p, entities: { ...p.entities, [k]: Math.max(1, Math.min(max, Number(e.target.value) || 1)) } }))} />
            </label>
          ))}
          <label className="flex items-center justify-between gap-3 text-sm py-1">
            <span>Warn before due (days)</span>
            <input type="number" min={0} max={180} className="qa-input w-20 text-right qa-mono" aria-label="Entity review warning days"
              value={s.entities.warnDays}
              onChange={(e) => set((p) => ({ ...p, entities: { ...p.entities, warnDays: Math.max(0, Math.min(180, Number(e.target.value) || 0)) } }))} />
          </label>
        </fieldset>
        <fieldset>
          <legend className="qa-eyebrow mb-2">Morning email</legend>
          <label className="flex items-center gap-2 text-sm py-1">
            <input type="checkbox" checked={s.digest.enabled} onChange={(e) => set((p) => ({ ...p, digest: { ...p.digest, enabled: e.target.checked } }))} />
            Send the Today list each morning
          </label>
          <label className="block text-sm py-1">
            Recipients (comma separated; blank = active advisers)
            <input className="qa-input w-full mt-1 text-sm" aria-label="Digest recipients"
              value={recipientsText ?? s.digest.recipients.join(', ')}
              onChange={(e) => {
                setRecipientsText(e.target.value);
                const list = e.target.value.split(',').map((x) => x.trim()).filter(Boolean);
                set((p) => ({ ...p, digest: { ...p.digest, recipients: list } }));
              }} />
          </label>
          <button type="button" className="qa-book-pop__link mt-2" onClick={testSend}>Send me a test now</button>
        </fieldset>
      </div>
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-hairline-paper">
        <button type="button" className="btn-primary text-sm" disabled={!draft} onClick={save}>Save settings</button>
        {draft && <button type="button" className="btn-ghost text-xs" onClick={() => { setDraft(null); setRecipientsText(null); }}>Discard</button>}
        {status && <span role="status" className="text-xs text-ink-soft">{status}</span>}
      </div>
    </div>
  );
}
